/**
 * TabsElement
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */
class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.onNavClick = this.onNavClick.bind(this);
    this.setActiveItem = this.setActiveItem.bind(this);
  }

  /**
   * Getters
   */

  /**
   * @type string
   */
  get id() {
    if (!this.getAttribute("id")) {
      this.setAttribute("id", `id-${Math.random().toString(16).slice(2)}`);
    }
    return /** @type {string} */ (this.getAttribute("id"));
  }

  /**
   * @type HTMLElement[]
   */
  get tabContent() {
    return Array.from(
      this.querySelectorAll(
        this.getAttribute("tab-content-selector") || ".tab__content",
      ),
    );
  }

  /**
   * @type HTMLElement
   */
  get tabButtons() {
    return Array.from(
      this.querySelectorAll(
        this.getAttribute("tab-button-selector") || ".tab__button",
      ),
    );
  }

  /**
   * @type string[]
   */
  get buttonActiveClass() {
    return this.getAttribute("button-active-class")?.split(" ") || [];
  }

  /**
   * @type string[]
   */
  get buttonInactiveClass() {
    return this.getAttribute("button-inactive-class")?.split(" ") || [];
  }

  /**
   * @type string[]
   */
  get contentActiveClass() {
    return this.getAttribute("content-active-class")?.split(" ") || [];
  }

  /**
   * @type string[]
   */
  get contentInactiveClass() {
    return this.getAttribute("content-inactive-class")?.split(" ") || [];
  }

  /**
   * connectedCallback
   * Render html/css & attatch event listeners
   */
  connectedCallback() {
    this.addAriaAttributes();

    this.tabButtons.forEach((item) => {
      // @ts-ignore
      item.addEventListener("click", this.onNavClick);
    });

    this.setActiveItem(0);
  }

  /**
   * disconnectedCallback
   * Remove listeners.
   */
  disconnectedCallback() {
    this.buttonNext?.removeEventListener("click", this.next);
    this.buttonPrevious?.removeEventListener("click", this.prev);
    this.tabButtons.forEach((item) => {
      // @ts-ignore
      item.removeEventListener("click", this.onNavClick);
      item.removeAttribute("aria-selected");
    });
  }

  /**
   * Set aria attributes on items and nav
   */
  addAriaAttributes() {
    this.setAttribute("role", "tablist");

    this.tabButtons.forEach((button, index) => {
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", "false");
      button.setAttribute("id", `${this.id}-nav-${index}`);
      button.setAttribute("aria-controls", `${this.id}-item-${index}`);
      button.setAttribute("type", "button");
    });

    this.tabContent.forEach((content, index) => {
      content.setAttribute("role", "tabpanel");
      content.setAttribute("id", `${this.id}-item-${index}`);
      content.setAttribute("aria-labelledby", `${this.id}-nav-${index}`);
    });
  }

  /**
   * Navigate to selected slide
   * @param {MouseEvent & { target: HTMLButtonElement }} event - Click event.
   */
  onNavClick(event) {
    event.preventDefault();
    const index = this.tabButtons.indexOf(event.currentTarget);
    this.setActiveItem(index);
  }

  /**
   * Removes aria-hidden and tabindex="-1" on active item.
   */
  setActiveItem(item) {
    this.tabButtons.forEach((button, index) => {
      if (index === item) {
        button.setAttribute("aria-selected", "true");
        button.classList.remove(...this.buttonInactiveClass);
        button.classList.add(...this.buttonActiveClass);
      } else {
        button.setAttribute("aria-selected", "false");
        button.classList.add(...this.buttonInactiveClass);
        button.classList.remove(...this.buttonActiveClass);
      }
    });

    this.tabContent.forEach((content, index) => {
      if (index === item) {
        content.setAttribute("aria-hidden", "false");
        content.classList.remove(...this.contentInactiveClass);
        content.classList.add(...this.contentActiveClass);
      } else {
        content.setAttribute("aria-hidden", "true");
        content.classList.add(...this.contentInactiveClass);
        content.classList.remove(...this.contentActiveClass);
      }
    });
  }
}

if (customElements.get("tabs-element") === undefined) {
  window.customElements.define("tabs-element", TabsElement);
}
